import { stripDuplicateTrailingHashtags } from '@mondaygpt/constants'
import { ThumbsUp, MessageCircle, Repeat2, Send, Copy, Sparkles } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useCallback } from 'react'
import { useBrandKitStore } from '../store/brand-kit-store'
import {
  useActiveEditingSectionStore,
  useEditorFocusStore,
  useSectionContextStore,
} from '../store/conversation-store'
import { type LinkedInPostContent } from '../types'
import type { MobileSelectedSection } from '../components/mobile-selection-types'
import { EditsApplyingBadge } from '../components/EditsApplyingBadge'
import { MobileSectionSelectButton } from '../components/MobileSectionSelectButton'
import { GeneratingMediaOverlay } from '../components/AssetMedia'
import { FeedPreviewReelFallback } from './FeedPreviewReelFallback'
import { getFeedVideoPreviewState, imageAspectStyle, isSocialPostEmpty } from './preview-utils'
import { SectionHoverWrapper } from './SectionHoverWrapper'
import { EditableInlineText } from './EditableInlineText'
import { EditableHashtags } from './EditableHashtags'
import { SectionActionBar } from './SectionActionBar'
import { CanvasMediaWithBar, useHasComposerCanvas } from '../composition/editor/ComposerCanvasMedia'
import { useCompositionLoading } from '../composition/editor/use-composition-loading'
import { useIsMobile } from 'hooks/use-mobile'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useCreativeStore } from '../store/creative-store'

const SKEL = 'rgba(15, 23, 42, 0.06)'
const SKEL_BG =
  'linear-gradient(110deg, rgba(15,23,42,0.03) 8%, rgba(15,23,42,0.07) 18%, rgba(15,23,42,0.03) 33%)'

interface LinkedInPostPreviewProps {
  content: LinkedInPostContent
  generatingFields?: Set<string>
  creativeId?: string
  selectedSectionId?: string | null
  onSectionSelect?: (section: MobileSelectedSection | null) => void
  interactive?: boolean
}

export function LinkedInPostPreview({
  content,
  generatingFields,
  creativeId,
  selectedSectionId,
  onSectionSelect,
  interactive = true,
}: LinkedInPostPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const logoUrl = useBrandKitStore(s => s.brandKit.logoId)
  const businessName = business?.name || 'Your Business'
  const setContext = useSectionContextStore(s => s.setContext)
  const requestFocus = useEditorFocusStore(s => s.requestFocus)
  const activeField = useActiveEditingSectionStore(s =>
    creativeId ? s.activeSections[creativeId] : null
  )

  const initials = businessName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  const hasImage = !!content.imageUrl
  const hasCanvas = useHasComposerCanvas()
  // Suppress the legacy hover while the composition is still loading, so it doesn't flash before
  // the live canvas mounts in its place.
  const compLoading = useCompositionLoading(creativeId)
  const hasReadyVideo = !!content.videoUrl
  const hasVideo = hasReadyVideo
  // Prefer the real image ratio (re-derived from the attachment) so the frame
  // matches the image; fall back to the format-based class when unknown.
  const imageStyle = imageAspectStyle(content.imageRatio)
  const aspectClass =
    content.format === 'portrait'
      ? 'aspect-[4/5]'
      : content.format === 'landscape'
        ? 'aspect-video'
        : 'aspect-square'
  const isMobile = useIsMobile()
  const readOnly = useComposerReadOnly()
  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)
  const editable = !!creativeId && !readOnly && !isMobile
  const hashtags = content.hashtags ?? []
  // Legacy LinkedIn content baked the tags into the caption AND hashtags[]; drop
  // the redundant trailing block so the caption + the blue tag row don't double up.
  const caption = stripDuplicateTrailingHashtags(content.caption, content.hashtags)
  const hasCaption = !!caption
  const hasHashtags = hashtags.length > 0
  const hashtagsText = hasHashtags ? hashtags.map(h => `#${h}`).join(' ') : ''

  const genVideo = generatingFields?.has('video') ?? false
  const genImage = generatingFields?.has('image') ?? false
  const genCaption = generatingFields?.has('caption') ?? false
  const genHashtags = generatingFields?.has('hashtags') ?? false
  const isGenerating = (generatingFields?.size ?? 0) > 0
  const { isGeneratingVideo, isFailedReel, isVideoMode, showCardOverlay } =
    getFeedVideoPreviewState(content, isGenerating, content.imageUrl)

  const handleSectionSelect = useCallback(
    (sectionType: string, label: string, preview: string) => {
      if (!onSectionSelect || !creativeId) return
      if (selectedSectionId === sectionType) {
        onSectionSelect(null)
      } else {
        onSectionSelect({
          sectionId: sectionType,
          sectionType,
          creativeType: 'linkedin_post',
          label,
          preview,
        })
      }
    },
    [creativeId, selectedSectionId, onSectionSelect]
  )

  const handleAskAI = useCallback(
    (field: string, label: string, preview: string) => {
      if (creativeId) {
        setContext(creativeId, { sectionId: field, sectionType: field, label, preview })
      }
    },
    [creativeId, setContext]
  )

  const handleEdit = useCallback(
    (field: string) => {
      if (creativeId) {
        requestFocus(creativeId, field)
      }
    },
    [creativeId, requestFocus]
  )

  if (isSocialPostEmpty(content)) {
    return (
      <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-400">
          <span className="text-4xl">💼</span>
          <p className="text-sm">Your LinkedIn post will appear here</p>
        </div>
      </div>
    )
  }

  // Reels render the media at a fixed 9:16 / 320px phone-frame. Shrink the
  // card to match so the header avatar/name don't sit left of the video
  // edge — see the matching note in InstagramPostPreview.

  return (
    <div
      className={`relative mx-auto w-full overflow-hidden rounded-lg bg-white transition-all duration-300 ${isVideoMode ? 'max-w-[320px]' : 'max-w-lg'}`}
      style={{
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {showCardOverlay && (
        <GeneratingMediaOverlay shape={isVideoMode ? 'video' : 'post'} tone="light" radius={8} />
      )}
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={businessName}
            className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-xs font-bold text-white">
            {initials}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900">{businessName}</p>
          <p className="text-xs text-gray-500">Just now</p>
        </div>
      </div>

      {/* Text — inline-editable on desktop; mobile keeps its tap→sheet flow. */}
      {editable && !genCaption ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(content.caption ?? '')
                  .catch(err => console.error('copy text failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () => handleAskAI('caption', 'Text', (content.caption ?? '').slice(0, 80)),
            },
          ]}
        >
          <div className="px-4 pb-3">
            <EditableInlineText
              initialValue={caption}
              onCommit={t => updateCreativeContent(creativeId!, { caption: t })}
              ariaLabel="Edit post text"
              placeholder="Write your post…"
              className="block text-sm whitespace-pre-line text-gray-900 outline-none focus:outline-none"
            />
          </div>
        </SectionActionBar>
      ) : hasCaption ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'caption' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect && handleSectionSelect('caption', 'Text', content.caption.slice(0, 80))
          }
        >
          <div className="relative overflow-hidden px-4 pb-3">
            {activeField === 'caption' && (
              <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
            )}
            <p className="text-sm whitespace-pre-line text-gray-900">{caption}</p>
          </div>
          {!genCaption && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'caption'}
              onSelect={() => handleSectionSelect('caption', 'Text', content.caption.slice(0, 80))}
            />
          )}
        </div>
      ) : genCaption ? (
        <div className="relative overflow-hidden px-4 pb-3">
          <div className="flex flex-col gap-2.5">
            <div className="h-2.5 w-full rounded-md" style={{ background: SKEL }} />
            <div className="h-2.5 w-3/4 rounded-md" style={{ background: SKEL }} />
          </div>
        </div>
      ) : null}

      {/* Hashtags — editable chips on desktop; mobile keeps its tap→sheet flow. */}
      {editable && !genHashtags ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(hashtags.map(h => `#${h}`).join(' '))
                  .catch(err => console.error('copy hashtags failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () => handleAskAI('hashtags', 'Hashtags', hashtagsText),
            },
          ]}
        >
          <div className="px-4 pb-3">
            <EditableHashtags
              value={hashtags}
              onCommit={h => updateCreativeContent(creativeId!, { hashtags: h })}
              className="text-[#0A66C2]"
            />
          </div>
        </SectionActionBar>
      ) : hasHashtags ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'hashtags' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect && handleSectionSelect('hashtags', 'Hashtags', hashtagsText)
          }
        >
          <div className="relative overflow-hidden px-4 pb-3">
            {activeField === 'hashtags' && (
              <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
            )}
            <p className="text-sm text-[#0A66C2]">{hashtagsText}</p>
          </div>
          {!genHashtags && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'hashtags'}
              onSelect={() => handleSectionSelect('hashtags', 'Hashtags', hashtagsText)}
            />
          )}
        </div>
      ) : genHashtags ? (
        <div className="relative overflow-hidden px-4 pb-3">
          <div className="h-2.5 w-24 rounded-md" style={{ background: SKEL }} />
        </div>
      ) : null}

      {/* Media (Video or Image) */}
      {hasCanvas && hasImage && !isVideoMode && !hasVideo ? (
        <CanvasMediaWithBar
          clipClassName="relative w-full overflow-hidden"
          interactive={interactive}
          onTap={() => handleSectionSelect('image', 'Image', 'Post media')}
        />
      ) : (
        <div
          className={`relative cursor-pointer overflow-hidden transition-all duration-200 md:cursor-default ${
            isVideoMode
              ? 'aspect-[9/16] w-full bg-black'
              : `${imageStyle ? '' : aspectClass} w-full`
          } ${selectedSectionId === 'image' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          style={isVideoMode ? undefined : imageStyle}
          onClick={() => onSectionSelect && handleSectionSelect('image', 'Image', 'Post media')}
        >
          {hasVideo ? (
            <>
              {!genVideo && creativeId ? (
                <SectionHoverWrapper
                  fieldId="video"
                  onAskAI={() => handleAskAI('video', 'Video', 'Post video')}
                  onEdit={() => handleEdit('video')}
                  isActiveEditing={activeField === 'video'}
                >
                  <video
                    src={content.videoUrl ?? undefined}
                    className="h-full w-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                </SectionHoverWrapper>
              ) : (
                <video
                  src={content.videoUrl ?? undefined}
                  className="h-full w-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              )}
              {!genVideo && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Video', 'Post video')}
                />
              )}
            </>
          ) : isFailedReel || (isGeneratingVideo && content.imageUrl) ? (
            <FeedPreviewReelFallback
              posterUrl={content.imageUrl}
              isFailedReel={isFailedReel}
              isGeneratingVideo={isGeneratingVideo}
            />
          ) : hasImage ? (
            <>
              {!genImage && creativeId && !compLoading ? (
                <SectionHoverWrapper
                  fieldId="image"
                  onAskAI={() => handleAskAI('image', 'Image', 'Post image')}
                  onEdit={() => handleEdit('image')}
                  isActiveEditing={activeField === 'image'}
                >
                  <img
                    src={content.imageUrl ?? undefined}
                    alt={content.altText || 'Post image'}
                    className="h-full w-full object-cover"
                  />
                </SectionHoverWrapper>
              ) : (
                <img
                  src={content.imageUrl ?? undefined}
                  alt={content.altText || 'Post image'}
                  className="h-full w-full object-cover"
                />
              )}
              {!genImage && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Image', 'Post image')}
                />
              )}
            </>
          ) : genImage || genVideo ? (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{ background: SKEL_BG }}
            >
              <div
                className="z-[1] flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: SKEL }}
              >
                <span className="text-2xl">{genVideo ? '🎬' : '🎨'}</span>
              </div>
            </div>
          ) : null}
          {/* Master-image fallback only (never the live canvas branch above):
            the rendered master lags the user's composition edits until the
            server re-renders it. Video branches excluded (flag never set). */}
          {content.compositionPendingRender &&
            !isGenerating &&
            !isVideoMode &&
            !hasVideo &&
            hasImage && <EditsApplyingBadge overlay creativeId={creativeId} />}
        </div>
      )}

      {/* Engagement bar */}
      <div className="flex items-center justify-around border-t px-4 py-2">
        <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50">
          <ThumbsUp size={16} />
          Like
        </button>
        <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50">
          <MessageCircle size={16} />
          Comment
        </button>
        <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50">
          <Repeat2 size={16} />
          Repost
        </button>
        <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50">
          <Send size={16} />
          Send
        </button>
      </div>
    </div>
  )
}
